import React, { useEffect, useRef, useState } from "react";
import { TextField, Button, CircularProgress } from "@mui/material";
import { Badge } from "@mui/icons-material";
import rootStyles from "../styles.module.scss";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { NotistackServices, UserServices } from "shared/services";
import { useAccount } from "shared/hooks";
import { UserAvatar } from "components";
import { isValidEmail, isValidUsername } from "shared/utils/Validate";

const AVATAR_MAX_BYTES = 5 * 1024 * 1024; // 5MB — mesmo limite do backend
const AVATAR_ACCEPTED_MIMES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export const ProfilePage: React.FC = () => {
	const user = useAccount();
	const [loading, setLoading] = useState(false);
	const [firstName, setFirstName] = useState(UserServices.currentUser?.firstName || "");
	const [lastName, setLastName] = useState(UserServices.currentUser?.lastName || "");
	const [username, setUsername] = useState(UserServices.currentUser?.username || "");
	const [email, setEmail] = useState(UserServices.currentUser?.email || "");

	// Estado do avatar: preview local imediato + flag de upload em curso.
	const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
	const [avatarUploading, setAvatarUploading] = useState(false);
	const [avatarRemoving, setAvatarRemoving] = useState(false);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		setFirstName(user?.firstName || "");
		setLastName(user?.lastName || "");
		setUsername(user?.username || "");
		setEmail(user?.email || "");
	}, []);

	const handleSaveChanges = async () => {
		setLoading(true);
		try {
			const displayName = `${firstName.trim()} ${lastName.trim()}`;
			await UserServices.updateUser({ displayName, username, email });
			NotistackServices.showSuccessMessage("Alterações salvas com sucesso!");
		} catch (error) {
			NotistackServices.showErrorMessage("Ocorreu um erro ao salvar as alterações. Tente novamente.");
		}
		setLoading(false);
	};

	// Aciona o file picker oculto.
	const handleChooseFile = () => {
		fileInputRef.current?.click();
	};

	// Validação client-side + preview imediato + upload real.
	const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		// Limpa o input para permitir re-selecionar o mesmo arquivo no futuro.
		event.target.value = "";
		if (!file) return;

		if (!AVATAR_ACCEPTED_MIMES.includes(file.type)) {
			NotistackServices.showErrorMessage("Formato inválido. Use PNG, JPG ou WEBP.");
			return;
		}
		if (file.size > AVATAR_MAX_BYTES) {
			NotistackServices.showErrorMessage("Arquivo muito grande. Máximo 5MB.");
			return;
		}

		// Preview otimista para feedback imediato.
		const previewUrl = URL.createObjectURL(file);
		setAvatarPreview(previewUrl);
		setAvatarUploading(true);

		try {
			await UserServices.uploadAvatar(file);
			NotistackServices.showSuccessMessage("Foto atualizada com sucesso!");
		} catch (err: any) {
			const msg = err?.message ?? "Falha ao enviar a foto. Tente novamente.";
			NotistackServices.showErrorMessage(msg);
			// Em caso de erro, descarta o preview para mostrar o avatar real.
			setAvatarPreview(null);
		} finally {
			setAvatarUploading(false);
			// Libera a URL temporária assim que o estado real reflete a nova foto.
			URL.revokeObjectURL(previewUrl);
		}
	};

	const handleRemoveAvatar = async () => {
		if (!user?.profilePictureUrl) return;
		setAvatarRemoving(true);
		setAvatarPreview(null);
		try {
			await UserServices.removeAvatar();
			NotistackServices.showSuccessMessage("Foto removida.");
		} catch (err: any) {
			const msg = err?.message ?? "Falha ao remover a foto.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setAvatarRemoving(false);
		}
	};

	const displayedImageUrl = avatarPreview ?? user?.profilePictureUrl ?? null;
	const hasAvatar = !!(displayedImageUrl);

	const isChanged = [firstName, lastName, username, email].join(",") !== [user?.firstName, user?.lastName, user?.username, user?.email].join(",");
	const isFormValid = firstName.trim() !== "" && lastName.trim() !== "" && isValidUsername(username) && isValidEmail(email);

	return (
		<div className={rootStyles.contentWrapper}>
			<div className={clsx(styles.avatarCard)}>
				<div className={styles.avatarContent}>
					<div className={styles.avatarImageWrapper}>
						<UserAvatar
							user={user}
							imageUrl={displayedImageUrl}
							size={96}
							ariaLabel="Sua foto de perfil"
						/>
						{avatarUploading && (
							<div className={styles.avatarOverlay} aria-hidden>
								<CircularProgress size={28} sx={{ color: "#fff" }} />
							</div>
						)}
					</div>
					<div className={styles.avatarActions}>
						<p>
							Atualize sua foto de perfil para ser identificado facilmente.
							<br />
							<span className={styles.avatarHint}>PNG, JPG ou WEBP — até 5MB.</span>
						</p>
						<input
							ref={fileInputRef}
							type="file"
							accept={AVATAR_ACCEPTED_MIMES.join(",")}
							onChange={handleFileSelected}
							style={{ display: "none" }}
							aria-hidden
						/>
						<div className={styles.buttons}>
							<Button
								variant="contained"
								sx={{
									backgroundColor: "white",
									color: "black",
									borderRadius: "9999px",
									fontWeight: "bold",
									textTransform: "none",
								}}
								onClick={handleChooseFile}
								disabled={avatarUploading || avatarRemoving}
							>
								{hasAvatar ? "Alterar Foto" : "Adicionar Foto"}
							</Button>
							<Button
								variant="outlined"
								sx={{
									color: "var(--mui-palette-text-secondary)",
									borderColor: "rgba(255, 255, 255, 0.1)",
									borderRadius: "9999px",
									fontWeight: "bold",
									textTransform: "none",
								}}
								onClick={handleRemoveAvatar}
								disabled={!hasAvatar || avatarUploading || avatarRemoving}
							>
								{avatarRemoving ? "Removendo..." : "Remover"}
							</Button>
						</div>
					</div>
				</div>
			</div>
			<section className={rootStyles.section}>
				<div className={rootStyles.card}>
					<h4 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
						<Badge style={{ color: "var(--mui-palette-primary-main)" }} />
						Informações Pessoais
					</h4>
					<div className={rootStyles.formGrid}>
						<div className={rootStyles.row}>
							<TextField
								fullWidth
								label="Primeiro Nome"
								value={firstName}
								variant="outlined"
								autoComplete="first-name"
								onChange={(e) => setFirstName(e.target.value)}
								error={firstName.trim() === ""}
								helperText={firstName.trim() === "" ? "O primeiro nome é obrigatório." : " "}
							/>
							<TextField
								fullWidth
								label="Sobrenome"
								value={lastName}
								variant="outlined"
								autoComplete="last-name"
								onChange={(e) => setLastName(e.target.value)}
								error={lastName.trim() === ""}
								helperText={lastName.trim() === "" ? "O sobrenome é obrigatório." : " "}
							/>
						</div>
						<div>
							<TextField
								fullWidth
								label="Username"
								value={username}
								variant="outlined"
								autoComplete="username"
								onChange={(e) => setUsername(e.target.value)}
								error={username.trim() === ""}
								helperText={username.trim() === "" ? "O username é obrigatório." : " "}
							/>
						</div>
						<div>
							<TextField
								fullWidth
								label="E-mail"
								value={email}
								variant="outlined"
								type="email"
								autoComplete="email"
								onChange={(e) => setEmail(e.target.value)}
								error={isValidEmail(email) === false}
								helperText={isValidEmail(email) === false ? "O e-mail não é válido." : " "}
								disabled
							/>
						</div>
					</div>
				</div>
			</section>

			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<Button
					variant="contained"
					color="primary"
					size="large"
					sx={{
						"borderRadius": "9999px",
						"padding": "1rem 2.5rem",
						"fontWeight": 800,
						"textTransform": "none",
						"boxShadow": "0 0 15px rgba(29, 185, 84, 0.3)",
						":disabled": {
							boxShadow: "none",
						},
					}}
					disabled={!isChanged || loading || !isFormValid}
					loading={loading}
					onClick={handleSaveChanges}
				>
					Salvar Alterações
				</Button>
			</div>
		</div>
	);
};
